import {
    createSelector,
    createStructuredSelector,
} from 'reselect';
import { initialState } from './reducer';
import { RootState } from '../../interface';
import {
    CableProviderType,
    Namespace,
} from '../../enum';
import { AppState } from './interface';
import { selectIsOpen } from '../Overlay/selector';
import {
    CUSTOMER_TYPE_UM,
    CUSTOMER_TYPE_VKD,
    CustomerType,
} from '@vfde-sails/constants';

const selectState = (state: RootState) => state[Namespace.APP] || initialState;

/**
 * Returns the selected tariff from the state.
 */
export const getSelectedTariff = (state: AppState) =>
    state.availableTariffs.find(tariff => tariff.id === state.selectedTariffId);

/**
 * Returns if customer is cable customer or not
 */
export const isCableCustomer = (customerType: CustomerType) =>
    customerType.includes(CUSTOMER_TYPE_UM) || customerType.includes(CUSTOMER_TYPE_VKD);

/**
 * Returns if the selected tariff available or not
 */
export const isSelectedAvailable = (state: AppState) => Boolean(state.availableTariffs.find(tariff =>
    tariff.id === state.preSelectedTariff?.id,
));

export const selectAppState = createSelector(selectState, state => state);
export const selectLoading = createSelector(selectState, state => Object.keys(state.loadingStatus).length > 0);
export const selectLoadingStatus = createSelector(selectState, state => state.loadingStatus);
export const selectError = createSelector(selectState, state => state.error);
export const selectGigakombi = createSelector(selectState, state => state.isGigakombi);
export const selectFriendsAndFamily = createSelector(selectState, state => state.isFriendsAndFamily);
export const selectYoung = createSelector(selectState, state => state.isYoung);
export const selectTVProductSelection = createSelector(selectState, state => state.isTVProductSelected);
export const selectSelectedTariffId = createSelector(selectState, state => state.selectedTariffId);
export const selectSelectedTVProduct = createSelector(selectState, state => state.selectedTVProduct);
export const selectAvailableTariffs = createSelector(selectState, state => state.availableTariffs);
export const selectInitialTariffs = createSelector(selectState, state => state.initialTariffs);
export const selectAlternativeTariff = createSelector(selectState, state => state.alternativeTariff);
export const selectAddress = createSelector(selectState, state => state.address);
export const selectCustomer = createSelector(selectState, state => state.customer);
export const selectSelectedTariff = createSelector(selectState, getSelectedTariff);
export const selectContentEnabled = createSelector(selectState, state => state.availableTariffs.length > 0);
export const selectAvailabilityStatus = createSelector(selectState, state => Boolean(state.address));
export const selectMarketability = createSelector(selectState, state => state.marketability);
export const selectBannerId = createSelector(selectState, state => state.bannerId);
export const selectAvailability = createSelector(selectState, state => state.availability);
export const selectResidentPlus = createSelector(selectState, state =>
    Boolean(getSelectedTariff(state)?.residentBonus) && !state.isFriendsAndFamily);
export const selectIsSelectedAvailable = createSelector(selectState,
    state => isSelectedAvailable(state));
export const selectPreSelectedTariff = createSelector(selectState, state => state.preSelectedTariff);
export const selectIsLoggedInCableCustomer = createSelector(selectState, state =>
    state.isLoggedIn && state.customerType && isCableCustomer(state.customerType));
export const selectProviderType = createSelector(selectState, state => {
    const selectedTariff = getSelectedTariff(state);

    return selectedTariff && (selectedTariff.isUnity ? CableProviderType.UME : CableProviderType.KDG);
});

export const mapStateToProps = createStructuredSelector({
    isLoading: selectLoading,
    loadingStatus: selectLoadingStatus,
    error: selectError,
    isGigakombi: selectGigakombi,
    isFriendsAndFamily: selectFriendsAndFamily,
    isYoung: selectYoung,
    isTVProductSelected: selectTVProductSelection,
    selectedTariffId: selectSelectedTariffId,
    selectedTVProduct: selectSelectedTVProduct,
    availableTariffs: selectAvailableTariffs,
    initialTariffs: selectInitialTariffs,
    alternativeTariff: selectAlternativeTariff,
    address: selectAddress,
    customer: selectCustomer,
    selectedTariff: selectSelectedTariff,
    isContentEnabled: selectContentEnabled,
    isAvailabilityChecked: selectAvailabilityStatus,
    marketability: selectMarketability,
    isResidentPlus: selectResidentPlus,
    isOverlayOpen: selectIsOpen,
    isSelectedAvailable: selectIsSelectedAvailable,
    preSelectedTariff: selectPreSelectedTariff,
    isLoggedInCableCustomer: selectIsLoggedInCableCustomer,
    bannerId: selectBannerId,
    providerType: selectProviderType,
    availability: selectAvailability,
});

/**
 * State props by selectors
 */
export type State = ReturnType<typeof mapStateToProps>;
